/**
 * escpos-formatter — Formatage ESC/POS pur pour VisionPOS.
 *
 * Ce module est SANS ÉTAT et sans dépendances externes (ni Supabase, ni React,
 * ni Capacitor). Il produit uniquement des Uint8Array ASCII 7 bits — le texte
 * est translittéré via escpos-ascii (accents dépouillés) car les imprimantes
 * interprètent les octets >= 0x80 selon leur codepage local (GBK sur Sunmi :
 * l'UTF-8 brut y imprimait des caractères chinois, é → 茅).
 *
 * INVARIANT 4.A.16 : les mentions NF525 doivent être byte-identiques avant et
 * après tout refactoring. Toute modification de la logique de formatage doit
 * être validée par un test E2E qui compare le buffer sortant byte par byte.
 *
 * Contenu :
 *   - wrapText()              — word-wrap adaptatif colonnes thermiques
 *   - buildEscPosReceipt()    — ticket caisse complet (NF525)
 *   - buildEscPosKitchen()    — bon cuisine/bar (KOT)
 *   - buildEscPosZReport()    — rapport Z/X clôture session
 */

import { ESC, GS } from './escpos-types';
import type { TicketData } from './escpos-types';
import { encodeEscPosAscii } from './escpos-ascii';
import { paymentRows } from './payment-labels';
import { zEncoursLines } from './z-encours-lines';
// Ré-exporté pour printer-adapters (évite un import supplémentaire dans ce fichier ratcheté)
export { paymentRows } from './payment-labels';
import { EDITOR, ticketFiscalMention } from '@/config/editor';
import { globalDiscountRows, formatTicketAmount } from './ticket-discount';

// ── Utilitaires ──────────────────────────────────────────────────────────────

/**
 * Word-wrap une chaîne à `width` colonnes, en cassant sur les espaces plutôt
 * qu'en milieu de mot. Les imprimantes thermiques (Sunmi AIDL, ESC/POS) coupent
 * à la limite de colonne sans égard aux mots — ce qui produit des artefacts
 * comme "TVA non applicable" → "TVA n\non applicable".
 */
export function wrapText(text: string, width: number): string[] {
  if (!text) return [];
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    if (paragraph.length <= width) {
      lines.push(paragraph);
      continue;
    }
    const words = paragraph.split(/\s+/);
    let current = '';
    for (const word of words) {
      if (!current) {
        current = word;
        continue;
      }
      if (current.length + 1 + word.length <= width) {
        current += ' ' + word;
        continue;
      }
      lines.push(current);
      // Mot isolé plus long que la largeur : coupage forcé.
      if (word.length > width) {
        for (let i = 0; i < word.length; i += width) lines.push(word.substring(i, i + width));
        current = '';
      } else {
        current = word;
      }
    }
    if (current) lines.push(current);
  }
  return lines;
}

// ── Receipt (ticket caisse NF525) ────────────────────────────────────────────

/**
 * Génère le buffer ESC/POS complet d'un ticket de caisse.
 *
 * INVARIANT 4.A.16 — ce buffer est la source de vérité fiscale imprimée.
 * Extraction par copie stricte depuis PrintService.ts lignes 149-268.
 * Aucune réécriture de logique — seul le nom de fichier change.
 */
export function buildEscPosReceipt(data: TicketData): Uint8Array {
  const ps = Math.max(16, Math.min(28, data.config.police_size || 20));
  // Colonnes par ligne selon largeur papier : 58mm → 32 cols, 80mm → 48 cols.
  const cpl = data.config.largeur_papier === 58 ? 32 : 48;
  const sep = '-'.repeat(cpl);
  const dsep = '='.repeat(cpl);

  const pad = (left: string, right: string): string => {
    const maxLeft = cpl - right.length - 1;
    const trimmedLeft = left.length > maxLeft ? left.substring(0, maxLeft) : left;
    const space = Math.max(1, cpl - trimmedLeft.length - right.length);
    return trimmedLeft + ' '.repeat(space) + right;
  };

  let t = '';
  t += ESC + '@'; // init
  t += ESC + 'a\x01'; // center

  // Bandeau DUPLICATA (NF525 art. 286 — copie identifiée comme telle)
  if (data.isDuplicate) {
    t += GS + '!' + '\x11'; // double width + double height
    t += ESC + 'E\x01'; // bold ON
    t += '*** DUPLICATA ***\n';
    t += ESC + 'E\x00'; // bold OFF
    t += GS + '!' + '\x00'; // reset size
    t += '\n';
  }

  // Header
  t += data.store_name + '\n';
  if (data.store_subtitle) t += data.store_subtitle + '\n';
  if (data.store_address) t += data.store_address + '\n';
  if (data.store_phone) t += 'Tel : ' + data.store_phone + '\n';
  if (data.store_email) t += data.store_email + '\n';
  if (data.store_siret) t += 'SIRET : ' + data.store_siret + '\n';
  if (data.tva_intracom) t += 'TVA Intra : ' + data.tva_intracom + '\n';

  // Meta
  t += sep + '\n';
  if (data.config.show_num_ticket && data.ticket_number) t += data.ticket_number + '\n';
  if (data.config.show_date && data.config.show_heure) t += data.date + ' ' + data.time + '\n';
  else if (data.config.show_date) t += data.date + '\n';
  else if (data.config.show_heure) t += data.time + '\n';
  if (data.config.show_vendeur) t += 'Vendeur : ' + data.cashier + '\n';
  t += data.register + '\n';
  if (data.config.show_client && data.client) t += 'Client : ' + data.client + '\n';

  // Items
  t += ESC + 'a\x00'; // left
  t += sep + '\n';
  for (const item of data.items) {
    if (data.config.show_ref_article && item.ref) t += '  ' + item.ref + '\n';
    t += pad(item.quantity + 'x ' + item.name, item.unit_price.toFixed(2) + ' EUR') + '\n';
  }
  t += sep + '\n';
  for (const row of globalDiscountRows(data))
    t += pad(row.label, formatTicketAmount(row.amount)) + '\n';

  // TVA
  if (data.config.show_detail_tva) {
    t += pad('Sous-total HT', data.total_ht.toFixed(2) + ' EUR') + '\n';
    if (data.config.show_tva_par_taux) {
      for (const tva of data.tva_breakdown) {
        t += pad('TVA ' + tva.rate + '%', tva.tva.toFixed(2) + ' EUR') + '\n';
      }
    }
  }

  // Total
  t += dsep + '\n';
  t += pad('TOTAL TTC', data.total_ttc.toFixed(2) + ' EUR') + '\n';
  t += dsep + '\n';

  // Payment — une ligne par moyen (fractionné) ou la ligne Reglement classique
  if (data.config.show_mode_reglement) {
    for (const row of paymentRows(data)) {
      t += pad(row.label, row.value) + '\n';
    }
    if (data.card_line) {
      // Réglage retour_tpe_detaille : une ligne « CB : VISA ****1234 · Aut. … · TID … »
      for (const l of wrapText(data.card_line, cpl - 2)) t += '  ' + l + '\n';
    } else if (data.card_scheme) {
      t += '  ' + data.card_scheme;
      if (data.pan_masked) t += ' PAN:' + data.pan_masked;
      if (data.auth_code) t += ' Auth:' + data.auth_code;
      t += '\n';
      if (data.aid) t += '  AID:' + data.aid;
      if (data.entry_mode) t += ' Mode:' + data.entry_mode;
      if (data.aid || data.entry_mode) t += '\n';
      if (data.tid) t += '  TID:' + data.tid;
      if (data.mid) t += ' MID:' + data.mid;
      if (data.tid || data.mid) t += '\n';
    }
  }

  // Footer — word-wrap long legal mentions so lines break on whitespace
  t += ESC + 'a\x01'; // center
  if (data.config.footer_legal)
    for (const l of wrapText(data.config.footer_legal, cpl)) t += l + '\n';
  if (data.config.footer_text)
    for (const l of wrapText(data.config.footer_text, cpl)) t += l + '\n';
  if (data.config.show_merci)
    for (const l of wrapText(data.config.merci_text || 'Merci de votre visite !', cpl))
      t += l + '\n';
  if (data.config.mention_agec)
    for (const l of wrapText(data.config.mention_agec, cpl)) t += l + '\n';
  for (const l of wrapText(ticketFiscalMention(data.config.mention_nf525), cpl)) t += l + '\n';

  // QR Code (placeholder texte — le vrai QR nécessite des commandes GS spécifiques)
  if (data.config.show_qr_code && data.config.qr_code_content) {
    t += '\n[QR: ' + data.config.qr_code_content + ']\n';
  }

  // Sceau de sécurité NF525
  if (data.config.show_hash_fiscal && data.fiscal_hash) {
    t += sep + '\n';
    t += 'SCEAU DE SECURITE\n';
    t += data.fiscal_hash.substring(0, Math.min(cpl, data.fiscal_hash.length)) + '\n';
    if (data.fiscal_hash.length > cpl) t += data.fiscal_hash.substring(cpl) + '\n';
    if (data.ticket_number) t += 'Ticket: ' + data.ticket_number + '\n';
  }

  t += '\n\n\n\n'; // feed avant coupe : 4 lignes pour que le bas du ticket
  t += GS + 'V\x01'; // (mentions légales / hash) passe le cutter — cf AIDL

  return encodeEscPosAscii(t);
}

// ── Kitchen / KOT ────────────────────────────────────────────────────────────

/** Données d'un bon cuisine (Kitchen Order Ticket). */
export type KitchenData = {
  destination: string;
  table_number?: string;
  order_number: string;
  server_name: string;
  items: Array<{ name: string; quantity: number; notes?: string }>;
  notes?: string;
  /** Service de l'envoi (Entrée/Plat/…) — affiché sous la destination. */
  course?: string;
  /** Bon de réclame (rappel d'un service) — bandeau RÉCLAME en tête. */
  reclame?: boolean;
};

/**
 * Génère le buffer ESC/POS d'un bon cuisine/bar.
 *
 * Bon LISIBLE DE LOIN (demande terrain WAVE 2026-07-09) : destination, table et
 * articles en double hauteur+largeur gras, cuissons/notes en double hauteur
 * grasse — aligné sur le rendu du chemin AIDL Sunmi (26pt gras) qui était déjà
 * correct. En double largeur : 16 colonnes utiles sur papier 80 mm (les noms
 * longs passent à la ligne, acceptable en cuisine).
 */
export function buildEscPosKitchen(data: KitchenData): Uint8Array {
  const SIZE_XL = GS + '!' + '\x11'; // double hauteur + double largeur
  const SIZE_TALL = GS + '!' + '\x01'; // double hauteur seule
  const SIZE_N = GS + '!' + '\x00'; // taille normale
  const BOLD_ON = ESC + 'E\x01';
  const BOLD_OFF = ESC + 'E\x00';

  let t = ESC + '@' + ESC + 'a\x01'; // init + center
  t += SIZE_XL + BOLD_ON;
  // Réclame : bandeau en toute première ligne — c'est l'info que la cuisine
  // doit voir avant tout (faire marcher le service, pas préparer un nouvel envoi).
  if (data.reclame) t += '>>> RECLAME <<<\n';
  t += '*** ' + data.destination + ' ***\n';
  // Service de l'envoi (appel) — la cuisine distingue entrées/plats de loin.
  if (data.course) t += '- ' + data.course.toUpperCase() + ' -\n';
  if (data.table_number) t += 'TABLE ' + data.table_number + '\n';
  t += SIZE_N + BOLD_OFF;
  t += 'Cmd #' + data.order_number + '\n';
  t += new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) + '\n';
  t += 'Serveur: ' + data.server_name + '\n';
  t += '================================\n';
  t += ESC + 'a\x00'; // left
  for (const item of data.items) {
    t += SIZE_XL + BOLD_ON + item.quantity + 'x ' + item.name + '\n' + SIZE_N + BOLD_OFF;
    // Cuissons / allergies : double hauteur grasse — critiques en cuisine.
    if (item.notes) t += SIZE_TALL + BOLD_ON + ' >> ' + item.notes + '\n' + SIZE_N + BOLD_OFF;
  }
  if (data.notes)
    t +=
      '--------------------------------\n' +
      SIZE_TALL +
      BOLD_ON +
      'NOTE: ' +
      data.notes +
      '\n' +
      SIZE_N +
      BOLD_OFF;
  // 4 lignes de feed avant coupe (BT/réseau) : le bas du bon passe le cutter
  // Sunmi (3 lignes coupaient le dernier article / la note). Cf ticket #251.
  t += '================================\n\n\n\n';
  t += GS + 'V\x01'; // partial cut
  return encodeEscPosAscii(t);
}

// ── Z/X Report ───────────────────────────────────────────────────────────────

/**
 * Payload d'un rapport Z (clôture fiscale) ou X (informatif) —
 * sous-ensemble typé de Record<string, unknown> pour la sécurité des accès.
 */
export type ZReportData = {
  store_name?: string;
  store_siret?: string;
  report_kind?: string;
  closure_number?: string | number;
  closure_date?: string;
  register_name?: string;
  user_name?: string;
  transaction_count?: number;
  total_ttc?: number;
  total_ht?: number;
  total_tva?: number;
  tva_details?: Array<{
    tva_rate?: number;
    total_ht?: number;
    total_tva?: number;
    total_ttc?: number;
  }>;
  payment_breakdown?: Record<string, number>;
  refunds_count?: number;
  refunds_total_ttc?: number;
  refunds_tva_details?: Array<{
    tva_rate?: number;
    total_ht?: number;
    total_tva?: number;
    total_ttc?: number;
  }>;
  refunds_payment_breakdown?: Record<string, number>;
  encours_settlements?: unknown;
  opening_amount?: number;
  closing_amount?: number;
  theoretical_cash?: number;
  cash_diff?: number;
  avg_ticket?: number;
  top_products?: Array<{ name?: string; qty?: number; total?: number }>;
  hash?: string;
  generated_at?: string;
  software_label?: string;
  editor?: string;
};

/**
 * Génère le buffer ESC/POS complet pour un rapport de session/clôture Z.
 * Utilisé par les adapters BT et Network. AIDL et Cloud Printer ont leur
 * propre formatage via printText (cf. printer-adapters.ts).
 *
 * Extraction par copie stricte depuis PrintService.ts lignes 301-420.
 */
export function buildEscPosZReport(closure: ZReportData): Uint8Array {
  const num = (k: keyof ZReportData) => Number(closure[k] ?? 0);
  const str = (k: keyof ZReportData) => String(closure[k] ?? '');
  let t = ESC + '@'; // init
  t += ESC + 'a\x01'; // align center
  t += GS + '!\x10'; // double-height for header
  t += (str('store_name') || 'VisionPOS') + '\n';
  t += GS + '!\x00'; // reset size
  if (closure.store_siret) t += 'SIRET ' + str('store_siret') + '\n';
  t += '================================\n';
  t += GS + '!\x10';
  const isXReport = String(closure.report_kind || '').includes('x');
  t += (isXReport ? 'RAPPORT X' : 'CLOTURE Z') + ' N°' + str('closure_number') + '\n';
  t += GS + '!\x00';
  if (closure.closure_date) {
    const d = new Date(String(closure.closure_date));
    t += d.toLocaleString('fr-FR') + '\n';
  }
  t += '================================\n';
  t += ESC + 'a\x00'; // align left
  if (closure.register_name) t += 'Caisse: ' + str('register_name') + '\n';
  if (closure.user_name) t += 'Operateur: ' + str('user_name') + '\n';
  t += '--------------------------------\n';
  t += 'Transactions: ' + num('transaction_count') + '\n';
  t += 'CA TTC      : ' + num('total_ttc').toFixed(2) + ' EUR\n';
  t += 'CA HT       : ' + num('total_ht').toFixed(2) + ' EUR\n';
  t += 'TVA         : ' + num('total_tva').toFixed(2) + ' EUR\n';

  // TVA par taux — affiche le montant de TVA (et la base HT) par taux,
  // pas le TTC (bug Z #12 : "TVA 20% : 122.89 EUR" au lieu de "20.48 EUR").
  if (Array.isArray(closure.tva_details) && closure.tva_details.length > 0) {
    t += '--------------------------------\n';
    t += 'Detail TVA:\n';
    for (const v of closure.tva_details) {
      const rate = Number(v?.tva_rate ?? 0).toFixed(1);
      const ht = Number(v?.total_ht ?? 0).toFixed(2);
      const tva = Number(v?.total_tva ?? 0).toFixed(2);
      t += '  ' + rate + '% (base ' + ht + ') : ' + tva + ' EUR\n';
    }
  }

  // Encaissements par mode
  if (closure.payment_breakdown && typeof closure.payment_breakdown === 'object') {
    t += '--------------------------------\n';
    t += 'Encaissements:\n';
    for (const [method, amount] of Object.entries(closure.payment_breakdown)) {
      t += '  ' + method.padEnd(20) + Number(amount).toFixed(2).padStart(10) + ' E\n';
    }
  }

  // Remboursements (avoirs) de la période — miroir AIDL/Cloud (4.A.16)
  if (num('refunds_count') > 0) {
    t += '--------------------------------\n';
    t += 'Remboursements: ' + num('refunds_count') + '\n';
    t += 'Total avoirs   : -' + num('refunds_total_ttc').toFixed(2) + ' EUR\n';
    if (
      closure.refunds_payment_breakdown &&
      typeof closure.refunds_payment_breakdown === 'object'
    ) {
      for (const [method, amount] of Object.entries(closure.refunds_payment_breakdown)) {
        t += '  ' + method.padEnd(20) + ('-' + Number(amount).toFixed(2)).padStart(10) + ' E\n';
      }
    }
  }

  // Règlements d'encours de la période, hors chiffre d'affaires — miroir AIDL/Cloud (4.A.16)
  const encoursLines = zEncoursLines(closure.encours_settlements, 20);
  if (encoursLines.length > 0) {
    t += '--------------------------------\n';
    for (const line of encoursLines) t += line + '\n';
  }

  t += '================================\n';

  // Fond de caisse (si dispo — session report)
  if (typeof closure.opening_amount === 'number' || typeof closure.closing_amount === 'number') {
    t += 'Fond ouverture : ' + Number(closure.opening_amount || 0).toFixed(2) + ' EUR\n';
    t += 'Especes theo.  : ' + Number(closure.theoretical_cash || 0).toFixed(2) + ' EUR\n';
    if (typeof closure.closing_amount === 'number') {
      t += 'Fond cloture   : ' + Number(closure.closing_amount).toFixed(2) + ' EUR\n';
    }
    if (typeof closure.cash_diff === 'number') {
      const diff = Number(closure.cash_diff);
      const sign = diff >= 0 ? '+' : '';
      t += 'Ecart caisse   : ' + sign + diff.toFixed(2) + ' EUR\n';
    }
    if (typeof closure.avg_ticket === 'number') {
      t += 'Panier moyen   : ' + Number(closure.avg_ticket).toFixed(2) + ' EUR\n';
    }
    t += '================================\n';
  }

  // Top produits (sur le ticket — utile pour suivi commercial)
  if (Array.isArray(closure.top_products) && closure.top_products.length > 0) {
    t += 'TOP VENTES\n';
    for (const p of closure.top_products) {
      const name = String(p?.name || '')
        .slice(0, 22)
        .padEnd(22);
      const qty = String(p?.qty || 0).padStart(3);
      const total = Number(p?.total || 0)
        .toFixed(2)
        .padStart(7);
      t += name + ' x' + qty + ' ' + total + 'E\n';
    }
    t += '================================\n';
  }

  // Hash NF525 (si fournie — clôture Z fiscale)
  if (closure.hash) {
    t += ESC + 'a\x01';
    t += String(closure.hash).slice(0, 32) + '\n';
    t += ESC + 'a\x00';
  }

  // Footer : généré le + éditeur + mention NF525
  t += ESC + 'a\x01';
  if (closure.generated_at) {
    t += 'Genere le ' + new Date(String(closure.generated_at)).toLocaleString('fr-FR') + '\n';
  }
  t += String(closure.software_label || EDITOR.nf525Mention) + '\n';
  t += 'Editeur : ' + String(closure.editor || EDITOR.legalName) + '\n';
  t += ESC + 'a\x00';

  t += '\n\n\n\n';
  t += GS + 'V\x01'; // partial cut
  return encodeEscPosAscii(t);
}
